import Vue from 'vue'

import {
  createNotificationRequestCoordinator,
  mergeUnreadSummary,
  NOTIFICATION_SOURCES,
} from './notification-center-domain.mjs'
import {
  loadNotificationPage,
  loadNotificationSummary,
  markAllNotificationsRead,
  markNotificationRead,
  resetNotificationSources,
  resolveNotificationTarget,
} from './notification-sources'

const emptySourceMap = factory => NOTIFICATION_SOURCES.reduce((result, source) => {
  result[source] = factory(source)
  return result
}, {})

const state = Vue.observable({
  ownerUserId: null,
  loading: false,
  itemsBySource: emptySourceMap(() => []),
  unreadBySource: emptySourceMap(() => 0),
  previewItems: [],
  totalUnread: 0,
  errors: emptySourceMap(() => null),
  lastLoadedAt: null,
})

let refreshPromise = null
let refreshPromiseGeneration = null
let contextGeneration = 0
let currentContext = {}
const requestCoordinator = createNotificationRequestCoordinator(NOTIFICATION_SOURCES)

function getNotificationContextGeneration() {
  return contextGeneration
}

function applySummary() {
  const summary = mergeUnreadSummary(state.itemsBySource, state.unreadBySource)
  state.totalUnread = summary.totalUnread
  state.previewItems = summary.previewItems
}

export const notificationCenterStore = {
  state,
  setContext(context = {}) {
    currentContext = { ...currentContext, ...context }
    const userId = context.userId ?? null
    if (userId !== null && String(state.ownerUserId) !== String(userId)) {
      this.reset({ permission: context.permission, userId })
    }
  },
  async refreshSummary(options = {}) {
    this.setContext(options)
    const generation = contextGeneration
    if (refreshPromise && refreshPromiseGeneration === generation) {
      if (!options.force) return refreshPromise
      return refreshPromise.then(() => this.refreshSummary({ ...options, force: false }))
    }
    state.loading = true
    const requests = NOTIFICATION_SOURCES.map(source => requestCoordinator.begin(source))
    const pendingRefresh = Promise.allSettled(
      NOTIFICATION_SOURCES.map(source => loadNotificationSummary(source, currentContext)),
    ).then(results => {
      if (generation !== contextGeneration) return state
      results.forEach((result, index) => {
        const source = NOTIFICATION_SOURCES[index]
        if (!requestCoordinator.isCurrent(requests[index])) return
        if (result.status === 'fulfilled') {
          Vue.set(state.itemsBySource, source, result.value.items)
          Vue.set(state.unreadBySource, source, Number(result.value.unreadCount || 0))
          Vue.set(state.errors, source, null)
        } else {
          Vue.set(state.errors, source, result.reason)
        }
      })
      applySummary()
      state.lastLoadedAt = new Date().toISOString()
      return state
    }).finally(() => {
      if (refreshPromise === pendingRefresh) {
        state.loading = false
        refreshPromise = null
        refreshPromiseGeneration = null
      }
    })
    refreshPromise = pendingRefresh
    refreshPromiseGeneration = generation
    return pendingRefresh
  },
  async refreshSource(source, options = {}) {
    if (!NOTIFICATION_SOURCES.includes(source)) throw new Error(`Unsupported notification source: ${source}`)
    this.setContext(options)
    const request = requestCoordinator.begin(source)
    try {
      const result = await loadNotificationSummary(source, currentContext)
      if (!requestCoordinator.isCurrent(request)) return result
      Vue.set(state.itemsBySource, source, result.items)
      Vue.set(state.unreadBySource, source, Number(result.unreadCount || 0))
      Vue.set(state.errors, source, null)
      applySummary()
      state.lastLoadedAt = new Date().toISOString()
      return result
    } catch (error) {
      if (requestCoordinator.isCurrent(request)) Vue.set(state.errors, source, error)
      throw error
    }
  },
  loadPage(source, options = {}) {
    return loadNotificationPage(source, {
      page: Math.max(1, Number(options.page) || 1),
      pageSize: Math.max(1, Number(options.pageSize) || 30),
    }, currentContext)
  },
  async markRead(item) {
    if (!item || item.isRead) return item
    await markNotificationRead(item)
    item.isRead = true
    Vue.set(state.unreadBySource, item.source, Math.max(0, Number(state.unreadBySource[item.source] || 0) - 1))
    Vue.set(state.itemsBySource, item.source, state.itemsBySource[item.source].map(row => (
      row.key === item.key ? { ...row, isRead: true } : row
    )))
    applySummary()
    return item
  },
  async markAllRead(source) {
    const generation = getNotificationContextGeneration()
    requestCoordinator.begin(source)
    const result = await markAllNotificationsRead(source, currentContext)
    if (generation !== getNotificationContextGeneration()) return result
    await this.refreshSource(source).catch(() => {})
    return result
  },
  resolveTarget(item) {
    return resolveNotificationTarget(item)
  },
  reset(options = {}) {
    contextGeneration += 1
    requestCoordinator.reset()
    currentContext = {
      userId: options.userId ?? null,
      permission: options.permission,
    }
    state.ownerUserId = options.userId ?? null
    state.loading = false
    state.itemsBySource = emptySourceMap(() => [])
    state.unreadBySource = emptySourceMap(() => 0)
    state.previewItems = []
    state.totalUnread = 0
    state.errors = emptySourceMap(() => null)
    state.lastLoadedAt = null
    resetNotificationSources(options.permission)
  },
}
